import React, { useEffect, useMemo, useState } from 'react';
import { IconInbox } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useSupportTickets, useTicketSummary, useTicketOptions } from '../../hooks/useSupportTickets';
import TicketTable from './components/TicketTable';
import { withoutEmpty, isForbidden, ITS_AGENT_PERMISSIONS } from './utils';

const QUEUES = [
    { key: 'unassigned', label: 'Unassigned', filter: { unassigned: true }, blurb: 'Nobody has picked these up yet.' },
    { key: 'assigned_to_me', label: 'Assigned to me', filter: { assigned_to: 'me' }, blurb: 'Tickets on your plate.' },
    { key: 'unresolved', label: 'All unresolved', filter: { unresolved: true }, blurb: 'New, open, pending and reopened.' },
];

// Same table as the ticket list, pinned to the queues an agent works from.
export default function SupportQueue() {
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;
    const isAgent = hasAnyPermission(ITS_AGENT_PERMISSIONS);

    const [activeQueue, setActiveQueue] = useState(QUEUES[0]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    const { data: options } = useTicketOptions({ enabled: isAgent });
    const { data: summary } = useTicketSummary(orgId, { enabled: isAgent });

    const filters = useMemo(() => withoutEmpty({
        ...activeQueue.filter,
        search: debouncedSearch,
        priority: priorityFilter,
        request_type: typeFilter,
    }), [activeQueue, debouncedSearch, priorityFilter, typeFilter]);

    const { data: tickets = [], isLoading, error } = useSupportTickets(orgId, filters);

    if (!isAgent || isForbidden(error)) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="Support Queue" path="Support / Queue" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    The support queue is available to IT support agents only.
                </div>
            </div>
        );
    }

    const subHeaderComponent = (
        <div className="flex gap-5 mb-10 w-full justify-end flex-wrap">
            <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none"
            >
                <option value="">All types</option>
                {(options?.request_type || []).map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none"
            >
                <option value="">All priorities</option>
                <option value="P1,P2">P1 + P2</option>
                {(options?.requested_priority || []).map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <input
                type="text"
                placeholder="Search tickets..."
                className="bg-bg-light border border-border w-full max-w-[250px] py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none"
                value={search}
                onChange={e => setSearch(e.target.value)}
            />
        </div>
    );

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title="Support Queue" path="Support / Queue" />

            <div className="grid md:grid-cols-3 grid-cols-1 gap-15">
                {QUEUES.map(queue => {
                    const isActive = activeQueue.key === queue.key;
                    return (
                        <button
                            key={queue.key}
                            onClick={() => setActiveQueue(queue)}
                            className={`bg-bg-dark rounded-lg shadow-light p-15 text-left border transition-colors ${isActive ? 'border-primary' : 'border-transparent hover:border-border'}`}
                        >
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-medium">{queue.label}</p>
                                <span className={`text-[20px]/[26px] font-semibold ${queue.key === 'unassigned' ? 'text-amber-500' : ''}`}>
                                    {summary?.[queue.key] ?? 0}
                                </span>
                            </div>
                            <p className="text-xs text-text-secondary mt-3">{queue.blurb}</p>
                        </button>
                    );
                })}
            </div>

            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                <div className="flex items-center gap-8 mb-20">
                    <IconInbox size={18} className="text-primary" />
                    <h2 className="text-lg font-bold">{activeQueue.label}</h2>
                </div>

                <TicketTable
                    tickets={tickets}
                    isLoading={isLoading}
                    subHeaderComponent={subHeaderComponent}
                    showRequester
                    emptyMessage="Nothing in this queue."
                />
            </div>
        </div>
    );
}
